/**
 * Dietary Filter for Collection Pages
 * Filters products by dietary restrictions (Gluten Free, Vegetarian, Vegan)
 */

class DietaryFilter {
  constructor() {
    this.filterButtons = document.querySelectorAll('.dietary-filter-btn');
    this.clearButton = document.querySelector('.dietary-filter-btn--clear');
    this.productCards = document.querySelectorAll('[data-product-card]');
    this.productGrid = document.querySelector('.collection-template__grid');
    this.activeFilters = new Set();

    // Get collection name from page title
    this.collectionName = this.getCollectionName();

    // Filter name mapping
    this.filterNames = {
      'dietary:glutenfree': 'Gluten Free',
      'dietary:vegetarian': 'Vegetarian',
      'dietary:vegan': 'Vegan'
    };

    if (this.filterButtons.length === 0 || this.productCards.length === 0) {
      return; // No filters or products on this page
    }

    this.init();
  }

  /**
   * Get collection name from page heading
   */
  getCollectionName() {
    const heading = document.querySelector('.collection-template__title');
    return heading ? heading.textContent.trim() : 'this collection';
  }

  init() {
    // Add click handlers to filter buttons
    this.filterButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const filter = btn.getAttribute('data-filter');

        if (filter === 'clear') {
          this.clearFilters();
        } else {
          this.toggleFilter(btn, filter);
        }
      });
    });
  }

  /**
   * Toggle a dietary filter on/off
   */
  toggleFilter(button, filter) {
    if (this.activeFilters.has(filter)) {
      // Remove filter
      this.activeFilters.delete(filter);
      button.classList.remove('active');
    } else {
      // Add filter
      this.activeFilters.add(filter);
      button.classList.add('active');
    }

    this.applyFilters();
    this.updateClearButton();
  }

  /**
   * Apply active filters to product cards
   */
  applyFilters() {
    if (this.activeFilters.size === 0) {
      // No filters active - show all products
      this.productCards.forEach(card => {
        card.style.display = '';
      });
      this.hideNoResultsMessage();
      return;
    }

    let visibleCount = 0;

    // Filter products
    this.productCards.forEach(card => {
      const tags = card.getAttribute('data-product-tags') || '';
      const productTags = tags.toLowerCase().split(',').map(t => t.trim());

      // Check if product matches ALL active filters (AND logic)
      // Special case: If "Vegetarian" is selected, also show "Vegan" items
      const matchesAll = Array.from(this.activeFilters).every(filter => {
        if (filter === 'dietary:vegetarian') {
          // Vegetarian filter matches both vegetarian AND vegan items
          return productTags.includes('dietary:vegetarian') || productTags.includes('dietary:vegan');
        }
        return productTags.includes(filter.toLowerCase());
      });

      // Show or hide the product
      if (matchesAll) {
        card.style.display = '';
        visibleCount++;
      } else {
        card.style.display = 'none';
      }
    });

    // Show no results message if no products match
    if (visibleCount === 0) {
      this.showNoResultsMessage();
    } else {
      this.hideNoResultsMessage();
    }

    // Show count of filtered products
    this.showFilteredCount();
  }

  /**
   * Clear all active filters
   */
  clearFilters() {
    this.activeFilters.clear();

    // Remove active class from all filter buttons
    this.filterButtons.forEach(btn => {
      btn.classList.remove('active');
    });

    // Show all products
    this.productCards.forEach(card => {
      card.style.display = '';
    });

    this.updateClearButton();
    this.hideFilteredCount();
    this.hideNoResultsMessage();
  }

  /**
   * Update clear button visibility
   */
  updateClearButton() {
    if (this.clearButton) {
      if (this.activeFilters.size > 0) {
        this.clearButton.style.display = '';
      } else {
        this.clearButton.style.display = 'none';
      }
    }
  }

  /**
   * Show count of filtered products
   */
  showFilteredCount() {
    const visibleCount = Array.from(this.productCards).filter(
      card => card.style.display !== 'none'
    ).length;

    // Create or update count message
    let countMessage = document.querySelector('.dietary-filter-count');
    if (!countMessage) {
      countMessage = document.createElement('div');
      countMessage.className = 'dietary-filter-count';

      const filtersContainer = document.querySelector('.dietary-filters');
      if (filtersContainer) {
        filtersContainer.appendChild(countMessage);
      }
    }

    countMessage.textContent = `Showing ${visibleCount} of ${this.productCards.length} products`;
    countMessage.style.display = '';
  }

  /**
   * Hide filtered count message
   */
  hideFilteredCount() {
    const countMessage = document.querySelector('.dietary-filter-count');
    if (countMessage) {
      countMessage.style.display = 'none';
    }
  }

  /**
   * Show "no results" message
   */
  showNoResultsMessage() {
    // Build dietary filter names list
    const filterNamesList = Array.from(this.activeFilters)
      .map(filter => this.filterNames[filter] || filter)
      .join(' and ');

    // Create or update no results message
    let noResultsMessage = document.querySelector('.dietary-filter-no-results');
    if (!noResultsMessage) {
      noResultsMessage = document.createElement('div');
      noResultsMessage.className = 'dietary-filter-no-results';

      if (this.productGrid) {
        this.productGrid.appendChild(noResultsMessage);
      }
    }

    noResultsMessage.innerHTML = `
      <div class="no-results-icon">🔍</div>
      <h3 class="no-results-title">No Items Found</h3>
      <p class="no-results-message">
        Sorry, we don't currently have any <strong>${filterNamesList}</strong> items in <strong>${this.collectionName}</strong>.
        We will be adding more options soon!
      </p>
      <button type="button" class="no-results-clear-btn" onclick="document.querySelector('.dietary-filter-btn--clear').click()">
        Clear Filters
      </button>
    `;
    noResultsMessage.style.display = 'block';
  }

  /**
   * Hide "no results" message
   */
  hideNoResultsMessage() {
    const noResultsMessage = document.querySelector('.dietary-filter-no-results');
    if (noResultsMessage) {
      noResultsMessage.style.display = 'none';
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new DietaryFilter();
  });
} else {
  new DietaryFilter();
}
